'use client';

import type { ReactNode, SyntheticEvent } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import type { AlertProps } from '@mui/material';
import { Alert, AlertTitle, Snackbar } from '@mui/material';

/**
 * NOTE:    This only currently supports a single snackbar at a time,
 *          fine for current use cases but we might need to handle multiple
 *          snackbars in future.
 */

export type Notification = {
  message: ReactNode;
  extraDetails?: ReactNode;
  severity: AlertProps['severity'];
};
type NotificationContext = {
  notification: Notification | null;
  setNotification: (notification: Notification | null) => void;
};
type Props = {
  children: ReactNode;
};
const SnackbarContext = createContext<NotificationContext>({
  notification: null,
  setNotification: () => undefined
});
export const useSnackbar = () => useContext(SnackbarContext);
export function SnackbarProvider({
  children
}: Props) {
  const [notification, setNotification] = useState<Notification | null>(null);
  return <SnackbarContext.Provider value={{
    notification,
    setNotification
  }} data-sentry-element="SnackbarContext.Provider" data-sentry-component="SnackbarProvider" data-sentry-source-file="SnackbarProvider.tsx">
      <SnackbarWrapper data-sentry-element="SnackbarWrapper" data-sentry-source-file="SnackbarProvider.tsx" />
      {children}
    </SnackbarContext.Provider>;
}
export function SnackbarWrapper() {
  const {
    notification,
    setNotification
  } = useSnackbar();
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    setIsVisible(!!notification);
  }, [notification]);
  function handleSnackbarClose(event: SyntheticEvent | Event, reason?: string) {
    if (reason !== 'clickaway') {
      handleClose();
    }
  }
  function handleClose() {
    setIsVisible(false);
    setTimeout(() => setNotification(null), 500);
  }
  return <Snackbar anchorOrigin={{
    vertical: 'top',
    horizontal: 'center'
  }} open={isVisible} autoHideDuration={10000} onClose={handleSnackbarClose} sx={{
    mt: 6
  }} data-sentry-element="Snackbar" data-sentry-component="SnackbarWrapper" data-sentry-source-file="SnackbarProvider.tsx">
      <Alert elevation={2} onClose={handleClose} severity={notification?.severity} variant="standard" sx={{
      width: '100%'
    }} data-sentry-element="Alert" data-sentry-source-file="SnackbarProvider.tsx">
        {notification?.extraDetails ? <>
            <AlertTitle>{notification.message}</AlertTitle>
            {notification.extraDetails}
          </> : notification?.message}
      </Alert>
    </Snackbar>;
}